import React, { useCallback, useRef, useState } from "react";
import Papa from "papaparse";
import classnames from "classnames";
import { Alert, Col, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";

import { useProfile } from "Components/Hooks/UserHooks";
import { importAssessments } from "common/services/assessments.service";
import {
  ASSESSMENT_FIELDS,
  FieldMapping,
  ImportResults,
  ImportStatus,
  MAX_IMPORT_RECORDS,
  autoMapFields,
  mapRowToRecord,
} from "./assessmentImportFields";

const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;
const PREVIEW_LIMIT = 10;

const AssessmentsImportWizard: React.FC = () => {
  const { token } = useProfile();

  const [activeTab, setActiveTab] = useState(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([1]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [firstLineIncludesFieldNames, setFirstLineIncludesFieldNames] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);

  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [importStatus, setImportStatus] = useState<ImportStatus>("idle");
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fileReady = !!selectedFile && csvRows.length > 0 && !csvParseError;
  const mappedFields = ASSESSMENT_FIELDS.filter((field) => !!fieldMappings[field.key]);

  const steps = [
    { tab: 1, label: "File Upload", icon: "ri-upload-cloud-2-line", locked: false },
    { tab: 2, label: "Field Mapping", icon: "ri-git-merge-line", locked: !fileReady },
    { tab: 3, label: "Data Preview", icon: "ri-eye-line", locked: !fileReady },
    { tab: 4, label: "Import", icon: "ri-upload-2-line", locked: !fileReady },
  ];

  const formatFileSize = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const toggleTab = (tab: number) => {
    if (activeTab === tab) return;
    if (tab < 1 || tab > 4) return;
    if (tab > 1 && !fileReady) return;
    setActiveTab(tab);
    setPassedSteps((prev) => [...prev, tab]);
  };

  const handlePickedFiles = (files: FileList | File[]) => {
    const file = Array.isArray(files) ? files[0] : files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setCsvParseError(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setFieldMappings({});

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length && results.data.length === 0) {
          setCsvParseError("Could not parse file. Please check the format.");
          return;
        }
        const headers = results.meta.fields ?? [];
        setCsvHeaders(headers);
        setCsvRows(results.data);
        setFieldMappings(autoMapFields(headers));
      },
      error: (error) => setCsvParseError(error.message),
    });
  };

  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const handleStartOver = () => {
    stopProgressSimulation();
    setSelectedFile(null);
    setIsDragActive(false);
    setFirstLineIncludesFieldNames(true);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvParseError(null);
    setFieldMappings({});
    setImportStatus("idle");
    setImportProgress({ current: 0, total: 0 });
    setImportResults(null);
    setImportErrors([]);
    setActiveTab(1);
    setPassedSteps([1]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const runImport = async () => {
    const rowsToImport = csvRows.slice(0, MAX_IMPORT_RECORDS);
    const total = rowsToImport.length;

    setImportStatus("running");
    setImportErrors([]);
    setImportProgress({ current: 0, total });

    let simulatedPercent = 0;
    progressIntervalRef.current = setInterval(() => {
      simulatedPercent = simulatedPercent + (90 - simulatedPercent) * 0.07;
      setImportProgress({
        current: Math.round((simulatedPercent / 100) * total),
        total,
      });
    }, 400);

    try {
      const records = rowsToImport.map((row) => mapRowToRecord(row, fieldMappings));
      const result = await importAssessments(
        { data: records },
        token,
        { timeoutMs: IMPORT_TIMEOUT_MS }
      );

      const successful = result.metadata?.data?.successful ?? 0;
      const failed = result.metadata?.data?.failed ?? 0;
      const downloadUrl =
        failed > 0 && result.blob && result.blob.size > 0
          ? URL.createObjectURL(result.blob)
          : null;

      setImportResults({ total, successful, failed, downloadUrl });
    } catch (error: any) {
      const isTimeout = error?.name === "AbortError";
      const message = isTimeout
        ? "The import request timed out. Try reducing the number of rows or increasing the server timeout."
        : error?.response?.data?.error?.message ??
          error?.message ??
          "Import request failed.";
      setImportErrors([message]);
      setImportResults({ total, successful: 0, failed: total, downloadUrl: null });
    } finally {
      stopProgressSimulation();
      setImportProgress({ current: total, total });
      setImportStatus("done");
    }
  };

  const progressPercent =
    importProgress.total > 0
      ? Math.round((importProgress.current / importProgress.total) * 100)
      : 0;

  const requiredMapped =
    !!fieldMappings.Title && !!fieldMappings.AssessmentType;
  const previewRows = csvRows.slice(0, PREVIEW_LIMIT);
  const effectiveRows = Math.min(csvRows.length, MAX_IMPORT_RECORDS);
  const isCapped = csvRows.length > MAX_IMPORT_RECORDS;
  const fileExt = selectedFile?.name.split(".").pop()?.toUpperCase() ?? "";

  return (
    <>
      <div className="step-arrow-nav mb-4">
        <Nav className="nav-pills nav-justified custom-nav" role="tablist">
          {steps.map(({ tab, label, icon, locked }) => (
            <NavItem key={tab} role="presentation">
              <NavLink
                href="#"
                className={classnames(
                  { active: activeTab === tab, done: passedSteps.includes(tab) && activeTab > tab },
                  "p-3 fs-15 d-flex align-items-center justify-content-center gap-2",
                  { "text-muted": locked }
                )}
                style={locked ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                title={locked ? "Complete the previous step to unlock" : label}
                onClick={() => toggleTab(tab)}
              >
                <span
                  className={classnames(
                    "d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0",
                    locked
                      ? "bg-light text-muted"
                      : activeTab > tab
                        ? "bg-success-subtle text-success"
                        : "bg-primary-subtle text-primary"
                  )}
                  style={{ width: 30, height: 30, fontSize: 15 }}
                >
                  {activeTab > tab ? (
                    <i className="ri-check-line" />
                  ) : locked ? (
                    <i className="ri-lock-line" />
                  ) : (
                    <i className={icon} />
                  )}
                </span>
                <span className="d-none d-sm-inline">{label}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={activeTab} className="pt-3">
        <TabPane tabId={1}>
          <div
            className="d-flex align-items-start gap-3 mb-4 pb-3"
            style={{ borderBottom: "1px solid #eef0f4" }}
          >
            <div
              className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 38, height: 38, background: "#eef2ff" }}
            >
              <i className="ri-upload-cloud-2-line" style={{ fontSize: 18, color: "#4361ee" }} />
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 fw-semibold">File Upload</h5>
              <p className="text-muted mb-0 mt-1">
                Upload a CSV or XLSX file containing assessment records.
              </p>
            </div>
            <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">
              Step 1 of 4
            </span>
          </div>

          <Row>
            <Col lg={12}>
              <div
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openFilePicker();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragActive(false);
                  if (e.dataTransfer?.files?.length) handlePickedFiles(e.dataTransfer.files);
                }}
                className="w-100 d-flex flex-column align-items-center justify-content-center"
                style={{
                  minHeight: 280,
                  background: isDragActive
                    ? "#f0f4ff"
                    : selectedFile && !csvParseError
                      ? "#f8fff8"
                      : "#f8f9fc",
                  border: `2px dashed ${
                    isDragActive
                      ? "#4361ee"
                      : selectedFile && !csvParseError
                        ? "#22c55e"
                        : csvParseError
                          ? "#ef4444"
                          : "#d1d9e6"
                  }`,
                  borderRadius: 10,
                  cursor: "pointer",
                  userSelect: "none",
                  outline: "none",
                  padding: "32px 24px",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    if (e.target.files?.length) handlePickedFiles(e.target.files);
                    e.target.value = "";
                  }}
                />

                {selectedFile ? (
                  <>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-2 mb-3 flex-shrink-0"
                      style={{
                        width: 56,
                        height: 56,
                        background: csvParseError ? "#fee2e2" : "#dcfce7",
                      }}
                    >
                      {csvParseError ? (
                        <i className="ri-error-warning-line" style={{ fontSize: 28, color: "#ef4444" }} />
                      ) : (
                        <i className="ri-file-text-line" style={{ fontSize: 28, color: "#16a34a" }} />
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span
                        className="fw-semibold"
                        style={{
                          fontSize: 15,
                          maxWidth: 340,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#1e293b",
                        }}
                        title={selectedFile.name}
                      >
                        {selectedFile.name}
                      </span>
                      {fileExt && (
                        <span
                          className="badge rounded-pill"
                          style={{
                            background: "#94a3b8",
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                          }}
                        >
                          {fileExt}
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 text-muted mb-3">
                      <span>{formatFileSize(selectedFile.size)}</span>
                      {!csvParseError && csvRows.length > 0 && (
                        <>
                          <span style={{ color: "#cbd5e1" }}>.</span>
                          <span>
                            <strong className="text-body">{csvRows.length.toLocaleString()}</strong> rows detected
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      className="form-check mb-3"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      style={{ cursor: "default" }}
                    >
                      <Input
                        id="assessment-first-line"
                        type="checkbox"
                        className="form-check-input"
                        checked={firstLineIncludesFieldNames}
                        onChange={(e) => setFirstLineIncludesFieldNames(e.target.checked)}
                      />
                      <Label className="form-check-label" htmlFor="assessment-first-line" style={{ color: "#64748b" }}>
                        First line includes field names
                      </Label>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mb-4"
                      style={{ width: 72, height: 72, background: isDragActive ? "#dbeafe" : "#f1f5f9" }}
                    >
                      <i
                        className="ri-upload-cloud-2-line"
                        style={{ fontSize: 34, color: isDragActive ? "#3b82f6" : "#64748b" }}
                      />
                    </div>
                    <div className="fw-semibold mb-1" style={{ fontSize: 16, color: "#64748b" }}>
                      {isDragActive ? "Drop your file here" : "Drag & drop your file here"}
                    </div>
                    <div style={{ fontSize: 14, color: "#64748b" }}>
                      or <span style={{ textDecoration: "underline" }}>click to browse</span>
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>

          {csvParseError && (
            <div
              className="d-flex align-items-start gap-2 rounded-2 mt-3 px-3 py-2"
              style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}
            >
              <i className="ri-error-warning-line text-danger flex-shrink-0 mt-1" />
              <span style={{ color: "#dc2626" }}>{csvParseError}</span>
            </div>
          )}

          <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
            <button type="button" className="btn btn-light btn-label" onClick={handleStartOver}>
              <i className="ri-restart-line label-icon align-middle fs-16 me-2" />
              Start Over
            </button>
            <button
              type="button"
              className="btn btn-primary btn-label right ms-auto"
              disabled={!selectedFile || !!csvParseError || csvRows.length === 0}
              onClick={() => toggleTab(2)}
            >
              <i className="ri-git-merge-line label-icon align-middle fs-16 ms-2" />
              Proceed to Field Mapping
            </button>
          </div>
        </TabPane>

        <TabPane tabId={2}>
          <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
            <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
              <i className="ri-git-merge-line" style={{ fontSize: 18, color: "#4361ee" }} />
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 fw-semibold">Field Mapping</h5>
              <p className="text-muted mb-0 mt-1">
                Map your file columns to assessment fields. Title and Assessment Type must be mapped to continue.
              </p>
            </div>
            <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">
              Step 2 of 4
            </span>
          </div>

          {csvHeaders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="ri-file-warning-line fs-1 mb-2 d-block" />
              No CSV headers found. Please go back and upload a valid file.
            </div>
          ) : (
            <Row>
              <Col lg={12}>
                <div className="d-flex align-items-center gap-3 rounded-2 px-3 py-2 mb-3" style={{ background: "#f8f9fc", border: "1px solid #e9ecef" }}>
                  <span>
                    <strong>{Object.keys(fieldMappings).length}</strong> of <strong>{ASSESSMENT_FIELDS.length}</strong> fields mapped
                  </span>
                  {!requiredMapped && (
                    <span className="d-flex align-items-center gap-1 text-danger">
                      <i className="ri-error-warning-line" />
                      Title and Assessment Type must be mapped
                    </span>
                  )}
                </div>
                <div className="rounded-3 border overflow-hidden">
                  <table className="table table-hover align-middle mb-0">
                    <thead style={{ background: "#f1f4f9" }}>
                      <tr>
                        <th className="py-3 ps-3" style={{ width: "38%" }}>Assessment Field</th>
                        <th className="py-3">CSV Column</th>
                        <th className="py-3 pe-3 text-center" style={{ width: 110 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ASSESSMENT_FIELDS.map((field) => {
                        const mapped = !!fieldMappings[field.key];
                        return (
                          <tr key={field.key}>
                            <td className="ps-3 py-2">
                              <span className="fw-medium">{field.label}</span>
                              {field.required && <span className="text-danger ms-1 fw-bold">*</span>}
                            </td>
                            <td className="py-2">
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 320 }}
                                value={fieldMappings[field.key] ?? ""}
                                onChange={(e) =>
                                  setFieldMappings((prev) => {
                                    const next = { ...prev };
                                    if (e.target.value) next[field.key] = e.target.value;
                                    else delete next[field.key];
                                    return next;
                                  })
                                }
                              >
                                <option value="">- Not mapped -</option>
                                {csvHeaders.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="text-center py-2 pe-3">
                              {mapped ? (
                                <span className="badge rounded-pill bg-success-subtle text-success">Mapped</span>
                              ) : (
                                <span className="badge rounded-pill bg-light text-muted">Skipped</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          )}

          <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
            <button type="button" className="btn btn-light btn-label" onClick={() => toggleTab(1)}>
              <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" />
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary btn-label right ms-auto"
              disabled={!requiredMapped}
              onClick={() => toggleTab(3)}
            >
              <i className="ri-eye-line label-icon align-middle fs-16 ms-2" />
              Preview Data
            </button>
          </div>
        </TabPane>

        <TabPane tabId={3}>
          <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
            <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
              <i className="ri-eye-line" style={{ fontSize: 18, color: "#4361ee" }} />
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 fw-semibold">Data Preview</h5>
              <p className="text-muted mb-0 mt-1">
                Showing the first {Math.min(PREVIEW_LIMIT, csvRows.length)} of <strong>{csvRows.length.toLocaleString()}</strong> rows.
              </p>
            </div>
            <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">
              Step 3 of 4
            </span>
          </div>

          {mappedFields.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="ri-table-line fs-1 mb-2 d-block" />
              No fields are mapped. Go back and map at least one field.
            </div>
          ) : (
            <div className="rounded-3 border" style={{ maxHeight: 420, overflowY: "auto", overflowX: "auto" }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead style={{ background: "#f1f4f9", position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th className="py-2 ps-3 text-center" style={{ minWidth: 46 }}>#</th>
                    {mappedFields.map((field) => (
                      <th key={field.key} className="py-2" style={{ whiteSpace: "nowrap" }}>
                        <div className="d-flex align-items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-danger fw-bold">*</span>}
                        </div>
                        <div className="fw-normal mt-1 text-muted" style={{ fontSize: 12 }}>
                          {fieldMappings[field.key]}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => (
                    <tr key={index}>
                      <td className="text-center ps-3 text-muted">{index + 1}</td>
                      {mappedFields.map((field) => {
                        const column = fieldMappings[field.key];
                        const value = column ? row[column] : undefined;
                        return (
                          <td
                            key={field.key}
                            style={{
                              maxWidth: 220,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={value ?? ""}
                          >
                            {value || "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
            <button type="button" className="btn btn-light btn-label" onClick={() => toggleTab(2)}>
              <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" />
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary btn-label right ms-auto"
              disabled={mappedFields.length === 0 || previewRows.length === 0}
              onClick={() => toggleTab(4)}
            >
              <i className="ri-upload-2-line label-icon align-middle fs-16 ms-2" />
              Proceed to Import
            </button>
          </div>
        </TabPane>

        <TabPane tabId={4}>
          {importStatus === "idle" && (
            <>
              <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
                <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
                  <i className="ri-upload-2-line" style={{ fontSize: 18, color: "#4361ee" }} />
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-0 fw-semibold">Ready to Import</h5>
                  <p className="text-muted mb-0 mt-1">Review the summary below, then click <strong>Start Import</strong>.</p>
                </div>
                <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">
                  Step 4 of 4
                </span>
              </div>

              {isCapped && (
                <div className="d-flex align-items-start gap-2 rounded-2 mb-4 px-3 py-3" style={{ background: "#fffbeb", border: "1px solid #fcd34d" }}>
                  <i className="ri-error-warning-line flex-shrink-0 mt-1" style={{ color: "#d97706" }} />
                  <span style={{ color: "#92400e" }}>
                    Your file contains <strong>{csvRows.length.toLocaleString()}</strong> rows. Only the first <strong>{MAX_IMPORT_RECORDS.toLocaleString()}</strong> records will be imported per request.
                  </span>
                </div>
              )}

              <Row className="g-3 mb-4">
                <Col sm={6} md={4}>
                  <div className="rounded-3 p-3 h-100" style={{ background: "#eef2ff", border: "1px solid #c7d2fe" }}>
                    <div className="text-uppercase fw-semibold mb-2 text-primary" style={{ fontSize: 13 }}>Rows to Import</div>
                    <div className="fw-bold" style={{ fontSize: 28 }}>{effectiveRows.toLocaleString()}</div>
                  </div>
                </Col>
                <Col sm={6} md={4}>
                  <div className="rounded-3 p-3 h-100" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <div className="text-uppercase fw-semibold mb-2 text-success" style={{ fontSize: 13 }}>Fields Mapped</div>
                    <div className="fw-bold" style={{ fontSize: 28 }}>{mappedFields.length}</div>
                  </div>
                </Col>
                <Col sm={12} md={4}>
                  <div className="rounded-3 p-3 h-100" style={{ background: "#faf5ff", border: "1px solid #e9d5ff" }}>
                    <div className="text-uppercase fw-semibold mb-2" style={{ fontSize: 13, color: "#7c3aed" }}>Required Fields</div>
                    <div className="fw-semibold">
                      {ASSESSMENT_FIELDS.filter((field) => field.required && fieldMappings[field.key]).length} of {ASSESSMENT_FIELDS.filter((field) => field.required).length} required fields mapped
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="d-flex align-items-center gap-3 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
                <button type="button" className="btn btn-light btn-label" onClick={handleStartOver}>
                  <i className="ri-restart-line label-icon align-middle fs-16 me-2" />
                  Start Over
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-label right ms-auto"
                  onClick={runImport}
                  disabled={csvRows.length === 0}
                >
                  Start Import
                  <i className="ri-upload-2-line label-icon align-middle fs-16 ms-2" />
                </button>
              </div>
            </>
          )}

          {importStatus === "running" && (
            <div className="text-center py-5">
              <div className="d-flex align-items-center justify-content-center mb-4">
                <div className="position-relative d-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
                  <div className="spinner-border text-primary position-absolute" style={{ width: 80, height: 80, borderWidth: 4 }} role="status">
                    <span className="visually-hidden">Importing...</span>
                  </div>
                  <i className="ri-upload-2-line text-primary" style={{ fontSize: 28 }} />
                </div>
              </div>
              <h5 className="fw-semibold mb-1">Importing...</h5>
              <p className="text-muted mb-4">
                Please wait while your assessment data is being processed.
              </p>
              <div className="mx-auto" style={{ maxWidth: 460 }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#64748b" }}>Progress</span>
                  <span className="fw-semibold text-primary">{progressPercent}%</span>
                </div>
                <div className="progress" style={{ height: 8, borderRadius: 99, background: "#e2e8f0" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${progressPercent}%`, borderRadius: 99 }}
                    aria-valuenow={progressPercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
            </div>
          )}

          {importStatus === "done" && importResults && (
            <div className="py-4">
              <div className="text-center mb-4">
                <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle" style={{ width: 72, height: 72, background: importResults.failed === 0 ? "#dcfce7" : "#fff7ed" }}>
                  <i
                    className={importResults.failed === 0 ? "ri-checkbox-circle-line" : "ri-alert-line"}
                    style={{ fontSize: 36, color: importResults.failed === 0 ? "#16a34a" : "#ea580c" }}
                  />
                </div>
                <h5 className="fw-semibold mb-1">
                  {importResults.failed === 0 ? "Import Complete" : "Import Finished with Errors"}
                </h5>
                <p className="text-muted mb-0">{importResults.total.toLocaleString()} records were processed.</p>
              </div>

              <Row className="g-3 justify-content-center mb-4">
                <Col xs="auto">
                  <div className="rounded-3 px-4 py-3 text-center" style={{ minWidth: 150, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <div style={{ fontSize: 32, fontWeight: 700, color: "#16a34a", lineHeight: 1 }}>{importResults.successful.toLocaleString()}</div>
                    <div className="mt-2 text-muted">Imported successfully</div>
                  </div>
                </Col>
                {importResults.failed > 0 && (
                  <Col xs="auto">
                    <div className="rounded-3 px-4 py-3 text-center" style={{ minWidth: 150, background: "#fff1f2", border: "1px solid #fecdd3" }}>
                      <div style={{ fontSize: 32, fontWeight: 700, color: "#e11d48", lineHeight: 1 }}>{importResults.failed.toLocaleString()}</div>
                      <div className="mt-2 text-muted">Failed to import</div>
                    </div>
                  </Col>
                )}
              </Row>

              {importResults.failed > 0 && importResults.downloadUrl && (
                <div className="d-flex align-items-center gap-3 rounded-2 px-4 py-3 mb-4 mx-auto" style={{ maxWidth: 520, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                  <i className="ri-file-download-line flex-shrink-0" style={{ fontSize: 22, color: "#ea580c" }} />
                  <div className="flex-grow-1">
                    <div className="fw-semibold" style={{ color: "#9a3412" }}>
                      {importResults.failed.toLocaleString()} record{importResults.failed !== 1 ? "s" : ""} could not be imported
                    </div>
                    <div className="mt-1" style={{ fontSize: 13, color: "#c2410c" }}>
                      Download the failed rows CSV to review and retry.
                    </div>
                  </div>
                  <a href={importResults.downloadUrl} download="assessment-import-errors.csv" className="btn btn-sm btn-outline-danger">
                    Download
                  </a>
                </div>
              )}

              {importErrors.length > 0 && (
                <Alert color="danger" className="text-start mb-4" style={{ maxWidth: 560, margin: "0 auto 1rem" }}>
                  <div className="fw-semibold mb-1 d-flex align-items-center gap-2">
                    <i className="ri-error-warning-line" />
                    Errors encountered
                  </div>
                  <ul className="mb-0 mt-2 ps-3">
                    {Array.from(new Set(importErrors)).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div className="text-center pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
                <button type="button" className="btn btn-light btn-label" onClick={handleStartOver}>
                  <i className="ri-restart-line label-icon align-middle fs-16 me-2" />
                  Start Over
                </button>
              </div>
            </div>
          )}
        </TabPane>
      </TabContent>
    </>
  );
};

export default AssessmentsImportWizard;
